import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Typography,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { RoomSummary } from '../../models/Room';
import { AdditionalGear, RequestedAdditionalGear } from '../../models/AdditionalGear';
import { getGearLabel, gearIcons } from '../../gears';
import { apiClient } from '../../publicApiClient';
import PaymentsIcon from '@mui/icons-material/Payments';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useTranslation } from 'react-i18next';
import { DiscountSettings, PaymentMethod } from '../../models/Organization';
import { getSingleBookingDiscountAmount, getSingleBookingDiscountedTotal } from '../../utils/discounts';
import BookingCancellationPolicy from '../BookingCancellationPolicy';
import type { CancellationPolicy } from '../../models/CancellationPolicy';
import type { PassWalletSummary } from '../../models/Pass';
import type { PassEligibilityEmptyReason } from '../../utils/passEligibility';
import PassTenderPicker, { PassTender } from '../passes/PassTenderPicker';
import SpaceTermsAcceptance from './SpaceTermsAcceptance';
import { formatBookingPaymentSlot } from '../../utils/bookingInstant';

/** Unified payment choice: money methods or pass. */
type PaymentChoice = PaymentMethod | 'pass';

/** Flat lesson price bill (tutor booking); skips gear fetch and room hourly math. */
export interface TutorLessonBill {
  lineLabel: string;
  durationMinutes: number;
  price: number;
  currencyCode: string;
}

interface PaymentDetailsStepProps {
  room: RoomSummary;
  duration: number;
  selectedAdditionalGears: RequestedAdditionalGear[];
  availableFromTime: string;
  /** When provided by the parent stepper, skips a duplicate availability fetch. */
  availableGear?: AdditionalGear[];
  selectedPaymentMethod?: PaymentMethod;
  onPaymentMethodChange?: (paymentMethod: PaymentMethod) => void;
  /** Number of weekly occurrences. undefined = single booking. */
  recurringWeeks?: number | null;
  /** Explicit session starts. When longer than one, this count is what the bill uses. */
  sessionStarts?: Date[] | null;
  /** Gear rented on each session, aligned with `sessionStarts`. Omit to bill the shared selection on every session. */
  sessionGears?: RequestedAdditionalGear[][] | null;
  discountSettings?: DiscountSettings | null;
  tutorLesson?: TutorLessonBill | null;
  /** Override when room stub has no policy (e.g. tutor flow from space summary). */
  cancellationPolicy?: CancellationPolicy | null;
  /** Override booking display timezone when room timezone is missing. */
  timezone?: string | null;
  /**
   * Wall-clock booking start in the room/space timezone (`yyyy-MM-ddTHH:mm[:ss]`).
   * Prefer this for cancellation deadlines — do not pass a UTC `toISOString()` value here.
   * Falls back to `availableFromTime` with a trailing `Z` stripped (legacy).
   */
  cancellationStartTime?: string | null;
  /** ACTIVE wallet passes eligible to cover this booking target. Omit/empty hides the tender toggle. */
  eligiblePasses?: PassWalletSummary[];
  eligiblePassesLoading?: boolean;
  /**
   * When there are no eligible passes: `insufficient_balance` shows a hint that a pass
   * covers this target but can't fund the duration. Other/empty hides Use pass as before.
   */
  passEligibilityEmptyReason?: PassEligibilityEmptyReason | null;
  /** Money | Pass tender choice; defaults to 'money' when omitted. */
  tender?: PassTender;
  onTenderChange?: (tender: PassTender) => void;
  selectedPassId?: string | null;
  onPassIdChange?: (passId: string) => void;
  /** Space regulation (text or URL). When set, user must accept before continuing. */
  spaceRegulation?: string;
  /** Space display name for the T&C acceptance label. */
  spaceName?: string;
  regulationAccepted?: boolean;
  onRegulationAcceptedChange?: (accepted: boolean) => void;
}

export default function PaymentDetailsStep({
  room,
  duration,
  selectedAdditionalGears,
  availableFromTime,
  availableGear: availableGearProp,
  selectedPaymentMethod,
  onPaymentMethodChange,
  recurringWeeks,
  sessionStarts,
  sessionGears,
  discountSettings,
  tutorLesson,
  cancellationPolicy,
  timezone,
  cancellationStartTime,
  eligiblePasses,
  eligiblePassesLoading = false,
  passEligibilityEmptyReason = null,
  tender = 'money',
  onTenderChange,
  selectedPassId = null,
  onPassIdChange,
  spaceRegulation,
  spaceName,
  regulationAccepted = false,
  onRegulationAcceptedChange,
}: PaymentDetailsStepProps) {
  const { t, i18n } = useTranslation();
  const usingSharedGear = availableGearProp !== undefined;
  const [fetchedGear, setFetchedGear] = useState<AdditionalGear[]>([]);
  const [loading, setLoading] = useState(!tutorLesson && !usingSharedGear);
  const availableGear = availableGearProp ?? fetchedGear;

  const resolvedCancellationPolicy =
    cancellationPolicy !== undefined ? cancellationPolicy : room.cancellationPolicy;
  const resolvedTimezone =
    timezone ?? room.timezone ?? room.spaceTimeZone ?? null;
  const resolvedCancellationStartTime = (
    cancellationStartTime ??
    availableFromTime.replace(/Z$/i, '').replace(/\.\d{3}$/, '')
  ).trim();

  // Set default payment method when component mounts or room changes
  useEffect(() => {
    if (room.supportedPaymentMethods && room.supportedPaymentMethods.length > 0 && selectedPaymentMethod === undefined) {
      // Prefer online checkout when the host offers both methods.
      const methods = room.supportedPaymentMethods;
      const defaultMethod = methods.includes(PaymentMethod.ONLINE)
        ? PaymentMethod.ONLINE
        : methods[0];
      onPaymentMethodChange?.(defaultMethod);
    }
  }, [room.supportedPaymentMethods, selectedPaymentMethod, onPaymentMethodChange]);

  // Fetch available gear only when the parent did not supply it (room bookings only)
  useEffect(() => {
    if (tutorLesson || usingSharedGear) {
      setLoading(false);
      return;
    }
    const fetchGear = async () => {
      setLoading(true);
      try {
        if (availableFromTime) {
          const gearData = await apiClient.getAvailableRoomGears(
            room.id,
            availableFromTime.replace('Z', ''),
            duration
          );
          setFetchedGear(gearData);
        }
      } catch (error) {
        console.error('Error fetching gear:', error);
      } finally {
        setLoading(false);
      }
    };

    void fetchGear();
  }, [room.id, availableFromTime, duration, tutorLesson, usingSharedGear]);

  const gearLists = sessionGears && sessionStarts && sessionStarts.length > 1 && sessionGears.length === sessionStarts.length
    ? sessionGears
    : null;
  const billedGearSelections = gearLists
    ? gearLists.flat()
    : selectedAdditionalGears;
  const selectedGear = billedGearSelections
    .filter((selection, index, all) =>
      all.findIndex((other) => other.id === selection.id && other.source === selection.source) === index,
    )
    .map((sel) => availableGear.find((g) => g.id === sel.id && g.source === sel.source))
    .filter((gear): gear is AdditionalGear => gear !== undefined);
  const sessionsIncludingGear = (gear: AdditionalGear) => {
    if (!gearLists) return occurrenceCount;
    return gearLists.filter((list) => list.some((selected) => selected.id === gear.id && selected.source === gear.source)).length;
  };

  const occurrenceCount =
    sessionStarts && sessionStarts.length > 1
      ? sessionStarts.length
      : recurringWeeks && recurringWeeks > 1
        ? recurringWeeks
        : 1;
  const durationMinutes = tutorLesson ? tutorLesson.durationMinutes : duration;
  const slotSource = availableFromTime.trim() || resolvedCancellationStartTime;
  const slotLabel = formatBookingPaymentSlot(
    slotSource,
    durationMinutes,
    i18n.language || 'en',
    availableFromTime.trim() ? resolvedTimezone : null,
  );

  const summaryDurationSecondary = (showOccurrenceCount: boolean) => {
    const multiSessionStarts =
      showOccurrenceCount && sessionStarts && sessionStarts.length > 1 ? sessionStarts : null;

    return (
      <Stack spacing={0.25} component="span">
        {/* Multi-session lists every occurrence below — skip the first-slot line to avoid repeating session 1 */}
        {!multiSessionStarts && slotLabel ? (
          <Typography variant="body2" color="text.secondary" component="span" display="block">
            {slotLabel}
          </Typography>
        ) : null}
        <Typography variant="body2" color="text.secondary" component="span" display="block">
          {t('payment.duration', 'Duration')}: {durationMinutes} {t('spaceCalendar.min', 'minutes')}
          {showOccurrenceCount && occurrenceCount > 1
            ? ` × ${occurrenceCount} ${t('payment.sessions', 'sessions')}`
            : null}
        </Typography>
        {multiSessionStarts
          ? multiSessionStarts.map((start, index) => {
              const end = new Date(start.getTime() + durationMinutes * 60_000);
              const locale = i18n.language || 'en';
              const day = start.toLocaleDateString(locale, {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              });
              const from = start.toLocaleTimeString(locale, {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              });
              const to = end.toLocaleTimeString(locale, {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              });
              return (
                <Typography
                  key={`${start.getTime()}-${index}`}
                  variant="body2"
                  color="text.secondary"
                  component="span"
                  display="block"
                >
                  {t('booking.sessionNumber', 'Session {{number}}', { number: index + 1 })}
                  {': '}
                  {`${day}, ${from}–${to}`}
                </Typography>
              );
            })
          : null}
      </Stack>
    );
  };

  const roomPrice = tutorLesson ? 0 : room.pricePerH * (duration / 60);
  const gearPrice = tutorLesson
    ? 0
    : selectedGear.reduce((total, gear) => {
        const perH = gear.pricePerH ?? 0;
        return total + (perH * (duration / 60));
      }, 0);
  const gearPriceTotal = tutorLesson
    ? 0
    : gearLists
      ? selectedGear.reduce((total, gear) => {
          const perSession = (gear.pricePerH ?? 0) * (duration / 60);
          return total + perSession * sessionsIncludingGear(gear);
        }, 0)
      : gearPrice * occurrenceCount;
  const singleSessionPrice = tutorLesson ? tutorLesson.price : roomPrice + gearPrice;
  const billCurrency = tutorLesson?.currencyCode ?? room.currecyCode ?? 'PLN';
  const totalPrice = tutorLesson
    ? tutorLesson.price * occurrenceCount
    : roomPrice * occurrenceCount + gearPriceTotal;
  const discountAmount = getSingleBookingDiscountAmount(totalPrice, duration, discountSettings, occurrenceCount);
  const discountedTotalPrice = getSingleBookingDiscountedTotal(totalPrice, duration, discountSettings, occurrenceCount);

  useEffect(() => {
    const singleBookingDiscount = discountSettings?.singleBookingDiscount;
    const durationHours = duration / 60;
    const totalDurationHours = durationHours * Math.max(1, occurrenceCount);
    const eligibleByDuration = singleBookingDiscount
      ? totalDurationHours >= singleBookingDiscount.minimumBookingHours
      : false;
    const enabled = Boolean(singleBookingDiscount?.enabled);
    const reasonNoDiscount = discountAmount > 0
      ? null
      : !singleBookingDiscount
      ? 'missing-singleBookingDiscount'
      : !enabled
      ? 'discount-disabled'
      : !eligibleByDuration
      ? 'duration-below-minimum'
      : 'unknown';

    console.debug('[PaymentDetailsStep] Discount calculation', {
      roomId: room.id,
      durationMinutes: duration,
      durationHours,
      totalDurationHours,
      baseTotal: Number(totalPrice.toFixed(2)),
      occurrenceCount,
      discountSettings: singleBookingDiscount ?? null,
      discountSettingsJson: singleBookingDiscount ? JSON.stringify(singleBookingDiscount) : null,
      enabled,
      eligibleByDuration,
      discountApplied: discountAmount > 0,
      discountAmount: Number(discountAmount.toFixed(2)),
      discountedTotal: Number(discountedTotalPrice.toFixed(2)),
      reasonNoDiscount,
    });
  }, [
    discountAmount,
    discountedTotalPrice,
    discountSettings,
    duration,
    occurrenceCount,
    room.id,
    totalPrice,
  ]);

  const showPassOption = Boolean(eligiblePassesLoading || (eligiblePasses && eligiblePasses.length > 0));
  const showInsufficientPassHint =
    !eligiblePassesLoading &&
    (!eligiblePasses || eligiblePasses.length === 0) &&
    passEligibilityEmptyReason === 'insufficient_balance';
  const supportedMethods = room.supportedPaymentMethods ?? [];
  const hasMultipleChoices = supportedMethods.length > 1 || showPassOption;
  const paymentChoice: PaymentChoice | null =
    tender === 'pass' ? 'pass' : (selectedPaymentMethod ?? null);
  const selectedPass =
    tender === 'pass' && selectedPassId
      ? (eligiblePasses ?? []).find((pass) => pass.id === selectedPassId)
      : undefined;

  const getPaymentMethodLabel = (method: PaymentMethod): string => {
    switch (method) {
      case PaymentMethod.CASH:
        return t('payment.cash', 'Pay on site');
      case PaymentMethod.ONLINE:
        return t('payment.online', 'Online payment');
      default:
        return method;
    }
  };

  const getPaymentMethodIcon = (method: PaymentMethod) => {
    switch (method) {
      case PaymentMethod.ONLINE:
        return <CreditCardIcon fontSize="small" />;
      case PaymentMethod.CASH:
        return <PaymentsIcon fontSize="small" />;
      default:
        return <PaymentsIcon fontSize="small" />;
    }
  };

  const handlePaymentChoice = (_: React.MouseEvent<HTMLElement>, value: PaymentChoice | null) => {
    if (!value) return;
    if (value === 'pass') {
      onTenderChange?.('pass');
      return;
    }
    if (tender !== 'money') onTenderChange?.('money');
    onPaymentMethodChange?.(value);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
        <Stack spacing={1.5}>
          {/* Unified payment choice: Online | Pay on site | Use pass */}
          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
              sx={{ rowGap: 1 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                {tender === 'pass' ? (
                  <ConfirmationNumberIcon color="primary" />
                ) : (
                  <PaymentsIcon color="primary" />
                )}
                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                  {showPassOption
                    ? t('payment.tender.label', 'How do you want to pay?')
                    : t('payment.method', 'Payment Method')}
                </Typography>
              </Stack>

              {loading ? (
                <Typography variant="body2" color="text.secondary">
                  {t('common.loading', 'Loading...')}
                </Typography>
              ) : supportedMethods.length === 0 && !showPassOption ? (
                <Typography variant="body2" color="error">
                  {t('payment.noMethodsAvailable', 'No payment methods available')}
                </Typography>
              ) : !hasMultipleChoices && supportedMethods.length === 1 ? (
                <Chip
                  icon={getPaymentMethodIcon(supportedMethods[0])}
                  label={getPaymentMethodLabel(supportedMethods[0])}
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: '0.9rem', py: 0.5 }}
                />
              ) : (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  color="primary"
                  value={paymentChoice}
                  onChange={handlePaymentChoice}
                  sx={{
                    flexShrink: 0,
                    '& .MuiToggleButton-root': {
                      px: 1.5,
                      py: 0.75,
                      fontSize: '0.85rem',
                      textTransform: 'none',
                      gap: 0.75,
                    },
                  }}
                >
                  {supportedMethods.map((method) => (
                    <ToggleButton key={method} value={method}>
                      {getPaymentMethodIcon(method)}
                      {getPaymentMethodLabel(method)}
                    </ToggleButton>
                  ))}
                  {showPassOption && (
                    <ToggleButton value="pass">
                      <ConfirmationNumberIcon fontSize="small" />
                      {t('payment.tender.usePass', 'Use pass')}
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
              )}
            </Stack>

            {tender === 'pass' && showPassOption && (
              <Box sx={{ mt: 1.5 }}>
                <PassTenderPicker
                  eligiblePasses={eligiblePasses ?? []}
                  eligiblePassesLoading={eligiblePassesLoading}
                  emptyReason={passEligibilityEmptyReason}
                  selectedPassId={selectedPassId}
                  onSelectPass={(passId) => onPassIdChange?.(passId)}
                />
              </Box>
            )}

            {showInsufficientPassHint ? (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                {t(
                  'payment.tender.insufficientBalance',
                  'You have a pass for this booking, but it doesn’t have enough time left.'
                )}
              </Alert>
            ) : null}
          </Box>

          <Divider />

          {/* Bill / booking summary */}
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <ReceiptIcon color="primary" />
              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                {tender === 'pass'
                  ? t('payment.tender.bookingSummary', 'Booking summary')
                  : t('payment.billBreakdown', 'Bill Breakdown')}
              </Typography>
            </Stack>

            {tender === 'pass' ? (
              <>
                <List dense disablePadding>
                  <ListItem sx={{ py: 0.5, pl: 0 }}>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1">
                          {tutorLesson ? tutorLesson.lineLabel : room.name}
                        </Typography>
                      }
                      secondary={summaryDurationSecondary(false)}
                    />
                  </ListItem>
                </List>
                {!tutorLesson && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {t(
                      'payment.tender.gearAndRecurringDisabledWithPass',
                      'Additional gear and recurring bookings aren\u2019t available when paying with a pass.',
                    )}
                  </Typography>
                )}
              </>
            ) : (
              <List dense disablePadding>
                {/* Room or tutor lesson line */}
                <ListItem sx={{ py: 0.5, pl: 0 }}>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1">
                        {tutorLesson ? tutorLesson.lineLabel : room.name}
                      </Typography>
                    }
                    secondary={summaryDurationSecondary(true)}
                  />
                  <Typography variant="subtitle1">
                    {(() => {
                      const linePrice = tutorLesson ? singleSessionPrice : roomPrice;
                      return occurrenceCount > 1
                        ? `${linePrice.toFixed(2)} × ${occurrenceCount} = ${(
                            linePrice * occurrenceCount
                          ).toFixed(2)} ${billCurrency}`
                        : `${linePrice.toFixed(2)} ${billCurrency}`;
                    })()}
                  </Typography>
                </ListItem>

                {/* Additional Gear */}
                {!tutorLesson && selectedGear.length > 0 && (
                  <>
                    <ListItem sx={{ pt: 0.5, pb: 0, pl: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Inventory2Icon color="primary" />
                        <Typography variant="subtitle1">{t('bookingDetails.additionalGear', 'Additional Gear')}</Typography>
                      </Stack>
                    </ListItem>
                    {selectedGear.map((gear) => {
                      const price = (gear.pricePerH ?? 0) * (duration / 60);
                      const count = sessionsIncludingGear(gear);
                      return (
                        <ListItem key={`${gear.source}:${gear.id}`} sx={{ pl: 3, py: 0.5 }}>
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center">
                                {gearIcons[gear.gearType]}
                                <Typography variant="body1">
                                  {getGearLabel(gear.gearType)}
                                </Typography>
                              </Stack>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                {gear.details}
                              </Typography>
                            }
                          />
                          <Typography variant="body1">
                            {count > 1
                              ? `${price.toFixed(2)} × ${count} = ${(price * count).toFixed(2)} ${billCurrency}`
                              : `${price.toFixed(2)} ${billCurrency}`}
                          </Typography>
                        </ListItem>
                      );
                    })}
                  </>
                )}

                {discountAmount > 0 && (
                  <ListItem sx={{ py: 0.5, pl: 0 }}>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <LocalOfferIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          <Typography variant="body2" color="success.main">
                            {t(
                              'payment.singleBookingDiscountAppliedWithThreshold',
                              '{{percent}}% discount for {{hours}}h+ bookings',
                              {
                                percent: discountSettings?.singleBookingDiscount?.discountPercent ?? 0,
                                hours: discountSettings?.singleBookingDiscount?.minimumBookingHours ?? 0,
                              }
                            )}
                          </Typography>
                        </Stack>
                      }
                    />
                    <Typography variant="body2" color="success.main">
                      -{discountAmount.toFixed(2)} {billCurrency}
                    </Typography>
                  </ListItem>
                )}
              </List>
            )}
          </Box>

          <Divider />

          {/* Total Section */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography variant="h6">{t('payment.totalAmount', 'Total Amount')}</Typography>
              {tender === 'pass' ? (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    sx={{
                      fontWeight: 'bold',
                      textDecoration: selectedPass ? 'line-through' : 'none',
                    }}
                  >
                    {discountedTotalPrice.toFixed(2)} {billCurrency}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    {selectedPass
                      ? t('payment.tender.coveredByPass', 'Covered by Pass')
                      : t('payment.tender.selectPass', 'Select a pass')}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                  {discountedTotalPrice.toFixed(2)} {billCurrency}
                </Typography>
              )}
            </Stack>
          </Box>

          {resolvedCancellationPolicy != null && resolvedCancellationStartTime ? (
            <>
              <Divider />
              <BookingCancellationPolicy
                startTime={resolvedCancellationStartTime}
                timezone={resolvedTimezone}
                policy={resolvedCancellationPolicy}
                showWhenMissing={false}
                density="comfortable"
                surface={false}
                sessionCount={occurrenceCount}
              />
            </>
          ) : null}

          {spaceRegulation?.trim() && onRegulationAcceptedChange ? (
            <>
              <Divider />
              <SpaceTermsAcceptance
                regulation={spaceRegulation}
                spaceName={spaceName}
                accepted={regulationAccepted}
                onAcceptedChange={onRegulationAcceptedChange}
              />
            </>
          ) : null}
        </Stack>
    </Box>
  );
} 